import React, { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { ZeosApiContext } from "../../../context/ZeosApiContext";
import { userDataSelector } from "../../../redux-store/UapiUserSlice";
import { IZeosUser } from "../../../services/auth/IZeosUser";
import { ZeosFontSize } from "../../../services/helpers/ThemeHelpers";
import { createLogger } from "../../../services/Logger";
import { IOnboardStatus } from "../../../services/nav/Interfaces";
import { IUapiReferral } from "../../../services/zeos-api/contracts/Onboarding";
import { OnboardContainer } from "../layout/OnboardContainer";

/**
 * Interface for UapiConfirmationScreen props
 */
interface IUapiConfirmationScreenProps {}

const log = createLogger("UapiConfirmation");

/**
 * UapiConfirmation screen component for the onboarding flow
 */
export const UapiConfirmationScreen: React.FC<
  IUapiConfirmationScreenProps
> = () => {
  const [referralCode, setReferralCode] = useState<string>("");
  const [referralData, setReferralData] = useState<IUapiReferral | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [zeosUser, setZeosUser] = useState<IZeosUser>();
  const [isBackPage, setIsBackPage] = useState<boolean>(false);

  const apiContext = useContext(ZeosApiContext);
  const userData = useSelector(userDataSelector);

  /**
   * Send user data back to OnboardTemplate
   */
  const handleUserCallback = (user: IZeosUser): void => {
    setZeosUser(user);
  };

  /**
   * Send navigation state data back to OnboardTemplate
   */
  const handleStatusCallback = (status: IOnboardStatus): void => {
    setIsBackPage(status.isBack);
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    //HACK: FOR DEVELOPMENT ONLY
    const queryReferral = "12345"; //params.get("referralCode");
    const storedReferral = localStorage.getItem("uapi_referral_code");
    const finalCode = queryReferral || storedReferral;
    if (finalCode && userData?.userId) {
      setReferralCode(finalCode);
      localStorage.setItem("uapi_referral_code", finalCode);
      loadReferralData(finalCode, userData.userId);
    }
  }, [userData?.username]);

  /**
   * Load referral data from the API
   * @param code - The referral code
   * @param zeosUserId - The user ID
   */
  const loadReferralData = async (
    code: string,
    zeosUserId: string
  ): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      await apiContext.Uapi.postReferralCodeLEGACY(zeosUserId, code);
      const response = await apiContext.Uapi.getReferralDataLEGACY(zeosUserId);
      setReferralData(response.data ?? null);
    } catch (err) {
      log.error("Failed to load referral data", err);
      setError("Failed to load referral data. Please try again.");
      setReferralData(null);
    } finally {
      setLoading(false);
    }
  };
  const handleClear = (): void => {
    localStorage.removeItem("uapi_referral_code");
    setReferralCode("");
    setReferralData(null);
    setError(null);
  };

  /**
   * Reload referral data
   */
  const handleReload = (): void => {
    if (referralCode && userData?.userId) {
      loadReferralData(referralCode, userData.userId);
    }
  };
  /**
   * Render a row in the confirmation table
   * @param label - The row label
   * @param value - The row value
   * @returns JSX element for the row
   */
  const renderRow = (label: string, value: string | undefined): JSX.Element => (
    <div className="flex gap-2">
      <p
        className={`font-semibold text-zeos-black w-[12rem] text-${ZeosFontSize.lg}`}
      >
        {label}
      </p>
      <p className={`text-zeos-black text-${ZeosFontSize.lg}`}>
        {value || "-"}
      </p>
    </div>
  );

  const content = (
    <div className="p-4 space-y-4">
      {!referralCode ? (
        <p>No referral code found.</p>
      ) : loading ? (
        <p>Loading referral data...</p>
      ) : error ? (
        <p className="text-red-600">{error}</p>
      ) : (
        <>
          <div className="col-span-12 flex flex-col gap-4">
            {renderRow("Referral Code:", referralCode)}
            {renderRow("User ID:", referralData?.uapiUserId)}
            {renderRow("Account ID:", referralData?.uapiAccountId)}
            {renderRow("Organization:", referralData?.organizationName)}
            {renderRow("Email:", referralData?.userEmail)}
            {renderRow("Utility:", referralData?.utility)}
            {renderRow("Created At (UTC):", referralData?.createdUtc)}
          </div>
          <div className="mt-4 flex gap-4">
            <button
              className="bg-gray-300 text-black px-4 py-2 rounded"
              onClick={handleClear}
            >
              Clear
            </button>
            <button
              className="bg-blue-600 text-white px-4 py-2 rounded"
              onClick={handleReload}
            >
              Reload
            </button>
          </div>
        </>
      )}
    </div>
  );

  /**
   * Handle next button click
   * @param doNavigate - Navigation callback function
   */
  const handleNext = async (doNavigate: () => void): Promise<void> => {
    try {
      // Mark step as complete and navigate
      if (zeosUser?.zeosUserId) {
        // No progress tracking needed
      }
      doNavigate();
    } catch (error) {
      log.error("Failed to complete utility confirmation step:", error);
    }
  };

  return (
    <OnboardContainer
      content={content}
      contentType="display"
      pageSlug="uapi-confirm"
      onNext={handleNext}
      userCallback={handleUserCallback}
      statusCallback={handleStatusCallback}
    />
  );
};
